'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'

/**
 * DS (Panda) port of `product-page-pl-renderer.module.scss`.
 *
 * The legacy SCSS is written in the fluid-rem system (root `0.6944vw` desktop / `2.6667vw` mobile, so
 * `1rem ≈ 10px`). That root is scoped to `html:where(:not([data-styles='panda']))` in `normalize.scss`,
 * so on the DS path the root falls back to the browser's 16px and every rem in that file inflates 1.6x
 * — the section hairline's `7.6rem` / `1.6rem` insets resolved to 121.6px / 25.6px instead of 76 / 16.
 * These Panda styles express the same layout in DS tokens.
 */

// Figma `.Widget-Divider` — 390 (`6236:52199`): x=16, w=358; 768 (`6236:52336`): x=16, w=736;
// 1920 desktop (`6228:43136`): x=208, w=1504. That is the DS content frame: full width minus the
// space-16 / space-48 gutter, capped at the 1504 content width (1600 − 2×48). Colour is the Figma
// `custom/black-trans-20` (#21201f33); no alpha token exists in the DS scale, so the literal rgba —
// the same value the sibling DS reco dividers use (`pair-with-products-ds.styles.ts`).
const DIVIDER_LINE = {
  content: '""',
  display: 'block',
  position: 'absolute',
  top: '0',
  left: '50%',
  transform: 'translateX(-50%)',
  width: {
    base: 'calc(100% - 2 * var(--web-ui-spacing-space-16))',
    lg: 'calc(100% - 2 * var(--web-ui-spacing-space-48))',
  },
  maxWidth: 'calc(1600px - 2 * var(--web-ui-spacing-space-48))',
  height: '1px',
  backgroundColor: 'rgba(33, 32, 31, 0.2)',
} as const

/*
 * Section dividers — pure layout CSS (no per-widget `showDivider` props, no JS).
 *
 * A "divider section" is a recommendation (PRODUCT_GRID_CAROUSEL, PRODUCT_LIST) or the reviews section
 * WITH reviews (REVIEWS_FULL). `:not([data-widget-type] *)` keeps it top-level; `:not(:empty)` drops
 * null-rendering shells.
 *
 * HORIZONTAL_PRODUCT_LIST is a full divider section for the `::before` rules as well as the MARGIN
 * ones. It used to be excluded from the former because it drew its OWN top hairline from
 * `product-reco-pdp.styles.ts` — but that line was unconditional, so it painted a seam wherever the
 * band landed, including directly under a content band (FEATURE_CARDS), where non-PL/legacy draws
 * none. Self-drawing put it outside the "first divider, or between two consecutive ones" logic below,
 * which is the only thing that knows whether a seam belongs. Its geometry was identical to
 * `DIVIDER_LINE`, so folding it in leaves every legitimate seam pixel-identical.
 *
 * Two cases draw a line, both mirroring non-PL:
 *   1. TOP SEAM — the FIRST divider section on the page separates the recommendations/reviews region
 *      from the product info above it.
 *   2. BETWEEN — two CONSECUTIVE divider sections get a line between them.
 *
 * Expressed as cascading rules: give the seam to EVERY divider, remove it from any divider that
 * already has one before it (`~` — leaves only the first), then restore it for adjacent pairs (`+`).
 * `~` and `+` share specificity, so the `+` restore is written last to win by source order.
 */
export const pageClass = css({
  // Establish a block formatting context so the LAST reco band's own `margin-bottom` (its footer gap)
  // is CONTAINED instead of collapsing out through the page container. `overflow-x: clip` does NOT
  // create a BFC, so without this the gap renders as 0 when a reco band is the last element.
  // STRFRNT-15896 / STRFRNT-15541.
  display: 'flow-root',
  // Mirrors the non-PL ProductPage's `clip-horizontal-scroll` so the page never scrolls horizontally.
  // `clip` (not `hidden`) is deliberate — it doesn't create a scroll container, so the gallery's
  // `position: sticky` still works.
  overflowX: 'clip',

  // (1) Seam candidate on every divider section. The seam gap is desktop-only — on mobile the
  //     reco/reviews sections carry their own top spacing (the hairline still draws on both).
  //     This margin is the SINGLE source of the gap above the first divider: the DS hero drops
  //     its own lg bottom padding on PL (`dsHeroRowFrameLgSeamOwnedClass`, ds-hero-row.styles.ts)
  //     so the two never stack. `space-48` — not legacy PL's 4.4rem — keeps the hero-to-divider
  //     gap identical to the non-PL DS surface, where STRFRNT-16252 settled on the standard
  //     space-48 hero bottom padding.
  '& :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty)':
    {
      position: 'relative',
      lg: { marginTop: 'space-48' },
    },
  '& :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty)::before':
    DIVIDER_LINE,

  // (2) Remove it from any divider that already has a divider section before it — leaving the line
  //     only on the FIRST divider (the top seam).
  '& :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty) ~ :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty)':
    {
      marginTop: '0',
    },
  '& :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty) ~ :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty)::before':
    {
      content: 'none',
    },

  // (3) Restore it between CONSECUTIVE divider sections (adjacent siblings).
  '& :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty) + :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty)':
    {
      // Legacy `4.4rem` at the legacy 10px desktop root = 44px. The DS spacing scale jumps 40 → 48,
      // so this stays a literal to preserve the existing seam gap exactly.
      lg: { marginTop: '44px' },
    },
  '& :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty) + :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty)::before':
    DIVIDER_LINE,

  // (4) STRFRNT-15804: reco bands already carry a large bottom padding, so the rule-(3) seam margin on
  //     the section AFTER a reco band double-spaces the gap vs non-PL. Zero it on ANY divider that
  //     FOLLOWS a reco band (covers reco→reco and the last reco→reviews seam). The hairline still
  //     draws; the reco's own bottom padding is the single source of the gap.
  '& :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"]):not([data-widget-type] *, :empty) + :is([data-widget-type="HORIZONTAL_PRODUCT_LIST"], [data-widget-type="PRODUCT_GRID_CAROUSEL"], [data-widget-type="PRODUCT_LIST"], [data-widget-type="REVIEWS_FULL"]):not([data-widget-type] *, :empty)':
    {
      lg: { marginTop: '0' },
    },

  /*
   * QSAL bottom-space fix (mobile). The HORIZONTAL_PRODUCT_LIST recommendation reuses the shared
   * pair-with styles, which reserve `min-height: 30.6rem` on mobile (sized for curated "Shop the Look"
   * content). Generic recommendation content is often shorter, so that reserved height shows as an
   * empty band below the carousel. The sibling reco widgets don't reserve height — match them.
   */
  lgDown: {
    '& [data-widget-type="HORIZONTAL_PRODUCT_LIST"] [class*="pairWithProducts"]': { minHeight: '0' },
  },
})
